class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.innerHTML = `
      <div class="wishlist-drawer__overlay" id="WishlistDrawer-Overlay"></div>
      <div class="drawer__inner" id="WishlistDrawer"></div>
    `;

    // Keyup ESC listener on global document to close the drawer seamlessly
    this._onDocumentKeyUp = (evt) => {
      if (evt.code === 'Escape' && this.classList.contains('active')) {
        this.close();
      }
    };
    document.addEventListener('keyup', this._onDocumentKeyUp);

    // Bind backdrop click or clicks on the container itself to close smoothly
    this.addEventListener('click', (event) => {
      if (
        event.target === this ||
        event.target.id === 'WishlistDrawer-Overlay' ||
        event.target.classList.contains('wishlist-drawer__overlay')
      ) {
        this.close();
      }
    });

    // Listen to custom wishlist updates
    document.addEventListener('wishlist:updated', () => {
      this.renderPremiumDrawer();
    });

    // Populate initial state components
    this.renderPremiumDrawer();

    // Hook standard accessibility on header icons
    this.setHeaderWishlistIconAccessibility();

    // Global event delegation as an absolute fallback to capture any wishlist clicks or bubble triggers
    document.addEventListener('click', (event) => {
      const wishlistTrigger = event.target.closest('#wj-wishlist-trigger') || event.target.closest('.wj-wishlist-trigger');
      if (wishlistTrigger) {
        event.preventDefault();
        event.stopPropagation();
        this.open(wishlistTrigger);
      }
    });
  }

  disconnectedCallback() {
    if (this._onDocumentKeyUp) {
      document.removeEventListener('keyup', this._onDocumentKeyUp);
    }
  }

  setHeaderWishlistIconAccessibility() {
    const wishlistLink = document.querySelector('#wj-wishlist-trigger') || document.querySelector('.wj-wishlist-trigger');
    if (!wishlistLink) return;

    wishlistLink.setAttribute('role', 'button');
    wishlistLink.setAttribute('aria-haspopup', 'dialog');

    // Replace default link navigation with drawer triggers
    const newWishlistLink = wishlistLink.cloneNode(true);
    wishlistLink.parentNode.replaceChild(newWishlistLink, wishlistLink);

    newWishlistLink.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.open(newWishlistLink);
    });
    newWishlistLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
        event.preventDefault();
        event.stopPropagation();
        this.open(newWishlistLink);
      }
    });

    // Sync count instantly
    if (window.Wishlist && typeof window.Wishlist.updateUI === 'function') {
      window.Wishlist.updateUI();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Sync theme mode with current header configuration
    const headerEl = document.querySelector('header-section');
    const currentTheme = headerEl ? headerEl.getAttribute('data-theme-mode') || 'dark' : 'dark';
    this.setAttribute('data-theme', currentTheme);

    // Render latest state items before slide-in starts
    this.renderPremiumDrawer();

    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 10);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    
    // Only remove body scroll lock if no other drawer (like cart-drawer) is active
    const cartActive = document.querySelector('cart-drawer.active');
    if (!cartActive) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Multi-layered resolver to grab titles, prices, vendors, and images
  getProductDetails(id) {
    // Layer 1: Resolve from global themeProducts
    if (window.themeProducts && window.themeProducts[id]) {
      return window.themeProducts[id];
    }

    // Layer 2: Scrape current active DOM card if present on active screen
    const domCard = document.querySelector(`.custom-premium-product-card[data-product-id="${id}"]`);
    if (domCard) {
      const titleEl = domCard.querySelector('.premium-product-card__title a') || domCard.querySelector('.premium-product-card__title');
      const titleText = titleEl ? titleEl.textContent.trim() : 'Premium Selection';
      
      const vendorEl = domCard.querySelector('.premium-product-card__vendor');
      const vendorText = vendorEl ? vendorEl.textContent.trim() : 'JAGS SIGNATURE';
      
      const priceEl = domCard.querySelector('.premium-product-card__price .price-item') || domCard.querySelector('.premium-product-card__price');
      const priceText = priceEl ? priceEl.textContent.trim() : '$120.00';
      
      const imgEl = domCard.querySelector('.product-card__image--primary');
      const imgSrc = imgEl ? imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || '' : '';
      
      const sizeBtn = domCard.querySelector('.quick-add-size-btn');
      const variantId = sizeBtn ? sizeBtn.getAttribute('data-variant-id') : `variant-${id}`;

      const details = {
        id: id,
        title: titleText,
        vendor: vendorText,
        price: priceText,
        image: imgSrc,
        url: titleEl ? titleEl.getAttribute('href') : '#',
        variantId: variantId
      };

      try {
        const cache = JSON.parse(localStorage.getItem('wearjags:wishlist_cache') || '{}');
        cache[id] = details;
        localStorage.setItem('wearjags:wishlist_cache', JSON.stringify(cache));
      } catch (e) {}

      return details;
    }

    // Layer 3: Resolve from sessionStorage cache
    try {
      const cache = JSON.parse(localStorage.getItem('wearjags:wishlist_cache') || '{}');
      if (cache[id]) return cache[id];
    } catch (e) {}

    // Layer 4: Standard fallback placeholder matching the premium catalog details
    return {
      id: id,
      title: 'Premium Selected Piece',
      vendor: 'JAGS SIGNATURE',
      price: '$50.00',
      image: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?q=80&w=300&auto=format&fit=crop',
      url: '#',
      variantId: `variant-${id}`
    };
  }

  renderPremiumDrawer() {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;

    // Read saved wishlist IDs
    const ids = (window.Wishlist && typeof window.Wishlist.getItems === 'function')
      ? window.Wishlist.getItems()
      : [];

    let removeOnAdd = true;
    try {
      const savedToggle = localStorage.getItem('wearjags:wishlist_remove_on_add');
      if (savedToggle !== null) {
        removeOnAdd = savedToggle === 'true';
      }
    } catch (e) {}

    // 1. Empty State Rendering
    if (ids.length === 0) {
      inner.innerHTML = `
        <div class="drawer__header-premium">
          <h2>Wishlist</h2>
          <button class="close-btn" aria-label="Close Wishlist">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
        <div class="drawer__empty-state">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.5;"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          <div class="empty-title">Wishlist is Empty</div>
          <p>Add products you love to save them for later.</p>
          <button class="continue-shopping-btn">Continue Exploring</button>
        </div>
      `;

      inner.querySelector('.close-btn').addEventListener('click', () => this.close());
      inner.querySelector('.continue-shopping-btn').addEventListener('click', () => this.close());
      return;
    }

    // 2. Active Wishlist Items Loop Rendering
    let itemsHtml = '';
    ids.forEach((id) => {
      const product = this.getProductDetails(id);

      itemsHtml += `
        <div class="cart-drawer-item-card" data-product-id="${id}" data-url="${product.url || '#'}" style="position: relative; cursor: pointer; display: flex; gap: 16px; padding: 16px 0; border-bottom: 1px solid var(--glass-divider, rgba(255, 255, 255, 0.08));">
          <div class="item-link-area" style="display: flex; gap: 16px; flex: 1; align-items: flex-start;">
            <div class="item-image-wrapper" style="width: 80px; aspect-ratio: 3 / 4; background: rgba(128, 128, 128, 0.08); border-radius: 4px; overflow: hidden; flex-shrink: 0; border: 1px solid var(--glass-border);">
               <img src="${product.image || 'https://images.unsplash.com/photo-1549298916-b41d501d3772?q=80&w=300&auto=format&fit=crop'}" alt="${product.title}" class="item-image" loading="lazy" referrerPolicy="no-referrer" style="width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s ease;">
            </div>
            <div class="item-details" style="flex: 1; display: flex; flex-direction: column; gap: 4px; padding-right: 28px;">
              <span class="item-vendor" style="font-size: 8.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.55; display: block; margin-bottom: 2px;">${product.vendor}</span>
              <h3 class="item-title" style="font-size: 13px; font-weight: 600; letter-spacing: 0.03em; margin: 0; line-height: 1.35; color: currentColor;">${product.title}</h3>
              <span class="item-price" style="font-size: 12px; font-weight: 700; letter-spacing: 0.04em; margin-top: 4px; color: currentColor;">${product.price}</span>
            </div>
          </div>
          
          <button class="remove-btn" data-product-id="${id}" aria-label="Remove item" style="position: absolute; right: 0; top: 12px; background: none; border: none; cursor: pointer; padding: 6px; color: currentColor; opacity: 0.55; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; border-radius: 4px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
        </div>
      `;
    });

    inner.innerHTML = `
      <div class="drawer__header-premium">
        <h2>Wishlist (${ids.length})</h2>
        <button class="close-btn" aria-label="Close Wishlist">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>
      
      <div class="drawer__body-premium">
        ${itemsHtml}
      </div>
      
      <div class="drawer__footer-premium">
        <p class="taxes-notice" style="margin: 0; text-align: center;">Save, compare and build your premium bespoke outfits at leisure.</p>
      </div>
    `;

    // Bind Close Action
    inner.querySelector('.close-btn').addEventListener('click', () => this.close());

    // Bind Entire Card Click Action for Navigation
    inner.querySelectorAll('.cart-drawer-item-card').forEach((card) => {
      card.addEventListener('click', (event) => {
        // Prevent navigation if the user clicked the remove button or its SVG path contents
        if (event.target.closest('.remove-btn')) {
          return;
        }
        const url = card.getAttribute('data-url');
        if (url && url !== '#') {
          window.location.href = url;
        }
      });
    });

    // Bind Remove Buttons
    inner.querySelectorAll('.remove-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const productId = btn.getAttribute('data-product-id');
        if (window.Wishlist && typeof window.Wishlist.toggle === 'function') {
          const product = this.getProductDetails(productId);
          window.Wishlist.toggle(productId);
          if (typeof showPremiumToast === 'function') {
            showPremiumToast(`${product.title} removed from wishlist`);
          }
        }
      });
    });
  }
}

customElements.define('wishlist-drawer', WishlistDrawer);
